import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// What this account may do, as the server sees it. The server is the authority:
// it checks the same permission on every admin endpoint, so this only decides
// what the screen offers, never what is allowed.
//
// There is deliberately no hard-coded fallback list. One used to live here, and
// it drifted out of step with the server - it still named permissions that no
// longer exist, which would have shown an admin buttons that always failed.
// If the list cannot be loaded we grant nothing and say so.
export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/me/permissions');
      setPermissions(response.data?.data?.permissions || []);
      setRole(response.data?.data?.role || null);
    } catch (err) {
      console.error('Could not load permissions:', err);
      setPermissions([]);
      setRole(null);
      setError('We could not load what this account is allowed to do.');
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (permission) => permissions.includes(permission);
  const hasAnyPermission = (list = []) => list.some((p) => permissions.includes(p));
  const hasAllPermissions = (list = []) => list.every((p) => permissions.includes(p));

  const isAdmin = () => role === 'admin' || role === 'super_admin';
  const isSuperAdmin = () => role === 'super_admin';
  const isManager = () => role === 'manager';
  const isEmployee = () => role === 'employee';

  const value = {
    permissions,
    role,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    isSuperAdmin,
    isManager,
    isEmployee,
    refreshPermissions: fetchPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export default PermissionContext;
